// Aba "Deploy" do painel Tecnologia — deriva Gitea × produção × dev das 3 stacks.
// Responde "o que já subiu?" sem abrir terminal. Backend: GET /api/system/deploy
// (proxy admin → twms-metrics-exporter, que roda no host e enxerga os checkouts).
// Sem polling: git não muda sozinho — carrega ao montar e no botão de atualizar.
import { useState, useEffect, useCallback } from 'react'
import { FiGitBranch, FiGitCommit, FiRefreshCw, FiAlertTriangle, FiCheck, FiExternalLink, FiLoader } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'

// dirty/behind/ahead podem vir `null` = "não deu pra verificar". NUNCA tratar
// null como zero: o painel existe pra dizer se produção está em dia, e um "sim"
// falso é o pior resultado possível dele.
interface Checkout { path: string; sha: string; short: string; subject: string; date: string | null; dirty: number | null }
interface Commit { short: string; subject: string; date: string | null }
interface AppDrift {
  app: string; label: string; repo: string; branch: string
  prod: Checkout | null; dev: Checkout | null; gitea: { sha: string; short: string } | null
  behind: number | null; ahead: number | null; pending: Commit[]
}
interface DeployData { ok: boolean; ts: string; gitea_url: string; apps: AppDrift[] }

const fmtDate = (d: string | null) => {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  } catch { return '—' }
}

// Uma linha "onde está o quê". `dirty` só aparece quando existe — é a informação
// que invalida o commit mostrado ao lado.
function Ref({ label, short, subject, date, dirty }: {
  label: string; short?: string; subject?: string; date?: string | null; dirty?: number | null
}) {
  return (
    <div className="flex items-baseline gap-2 min-w-0 py-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground w-[68px] shrink-0">{label}</span>
      <code className="text-[11px] font-mono text-primary shrink-0" title={short ? `commit ${short}` : undefined}>{short || '—'}</code>
      <span className="text-[12px] text-foreground truncate flex-1 min-w-0" title={subject}>{subject || '—'}</span>
      {dirty === null && short && (
        <span className="text-[10px] font-semibold text-muted-foreground shrink-0"
          title="Não foi possível verificar se há arquivo não commitado neste checkout">sujo?</span>
      )}
      {!!dirty && (
        <span className="text-[10px] font-semibold text-red-600 dark:text-red-400 shrink-0"
          title={`${dirty} arquivo(s) alterado(s) e não commitado(s) — o commit acima não descreve o que está rodando`}>
          {dirty} sujo{dirty > 1 ? 's' : ''}
        </span>
      )}
      {date !== undefined && <span className="text-[10px] text-muted-foreground tabular-nums shrink-0 hidden sm:inline">{fmtDate(date ?? null)}</span>}
    </div>
  )
}

const CHIP = 'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full'

// Vários alertas podem valer ao mesmo tempo (produção suja E atrasada, por
// exemplo). Mostrar só o primeiro faria o pior estado parecer menos grave que o
// segundo pior — então cada condição vira um chip próprio.
function Badges({ a }: { a: AppDrift }) {
  const chips: React.ReactNode[] = []
  if (a.prod?.dirty) chips.push(
    <span key="dirty" className={`${CHIP} bg-red-500/12 text-red-600 dark:text-red-400`}
      title="Produção tem arquivo alterado fora do git — o deploy não é reprodutível a partir do commit">
      <FiAlertTriangle className="w-3 h-3" /> produção suja
    </span>)
  if (a.ahead) chips.push(
    <span key="ahead" className={`${CHIP} bg-red-500/12 text-red-600 dark:text-red-400`}
      title={`${a.ahead} commit(s) existem só no disco de produção e nunca foram enviados ao Gitea — se a máquina morrer, esse código some`}>
      <FiAlertTriangle className="w-3 h-3" /> {a.ahead} não enviado{a.ahead > 1 ? 's' : ''}
    </span>)
  if (a.behind) chips.push(
    <span key="behind" className={`${CHIP} bg-amber-500/12 text-amber-600 dark:text-amber-400`}
      title={`Existem ${a.behind} commit(s) no Gitea que ainda não subiram pra produção`}>
      <FiAlertTriangle className="w-3 h-3" /> {a.behind} atrás
    </span>)
  if (a.behind === null || a.ahead === null || a.prod?.dirty === null) chips.push(
    <span key="unk" className={`${CHIP} bg-muted text-muted-foreground`}
      title="Não foi possível comparar produção com o Gitea — não dá pra afirmar que está em dia">
      indeterminado
    </span>)
  if (!chips.length) chips.push(
    <span key="ok" className={`${CHIP} bg-emerald-500/12 text-emerald-600 dark:text-emerald-400`}
      title="Produção está no mesmo commit que o Gitea, sem alteração solta">
      <FiCheck className="w-3 h-3" /> em dia
    </span>)
  return <div className="flex flex-wrap items-center gap-1 justify-end">{chips}</div>
}

export function DeployPanel() {
  const { authenticatedFetch } = useAuth()
  const [data, setData] = useState<DeployData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const [forbidden, setForbidden] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.SYSTEM_DEPLOY)
      if (r.status === 403) { setForbidden(true); return }
      const d = await r.json()
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      setData(d)
    } catch (e) { setError((e as Error).message) }
    finally { setLoading(false) }
  }, [authenticatedFetch])
  useEffect(() => { load() }, [load])

  if (forbidden) return (
    <div className="h-full flex items-center justify-center text-muted-foreground">
      <div className="text-center"><FiGitBranch className="w-7 h-7 mx-auto mb-2 opacity-40" /><p className="text-sm">Painel restrito a administradores.</p></div>
    </div>
  )

  return (
    <div className="h-full min-h-0 flex flex-col overflow-y-auto">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-4 flex-1 min-h-0">
        <div className="flex items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <FiGitBranch className="w-5 h-5 text-primary" />
            <h2 className="text-base font-bold text-foreground">Versões e deploy</h2>
            <span className="text-xs text-muted-foreground hidden sm:inline">produção × dev × Gitea</span>
          </div>
          <div className="flex items-center gap-2">
            {data?.gitea_url && (
              <a href={data.gitea_url} target="_blank" rel="noreferrer" title="Abrir o Gitea"
                className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary transition-colors">
                <FiExternalLink className="w-3.5 h-3.5" /> Gitea
              </a>
            )}
            <button onClick={load} disabled={loading} title="Atualizar"
              className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-50">
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {error && (
          <div className="px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-700 dark:text-red-300">
            Falha ao ler o estado do git: {error}
          </div>
        )}
        {loading && !data && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground text-sm">
            <FiLoader className="w-4 h-4 animate-spin" /> Consultando os repositórios…
          </div>
        )}

        {/* Os cards têm altura própria (auto-rows-min); a grade é que rola,
            quando a lista de pendentes de algum app crescer. */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 flex-1 min-h-[220px] overflow-y-auto auto-rows-min pr-1">
          {data?.apps.map(a => (
            <div key={a.app} className="glass-panel neu-card rounded-2xl p-5 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-sm font-semibold text-foreground">{a.label}</span>
                  <code className="text-[10px] text-muted-foreground truncate" title={`${a.repo} · branch ${a.branch}`}>{a.repo}</code>
                </div>
                <Badges a={a} />
              </div>

              <div className="flex flex-col divide-y divide-border/40">
                <Ref label="Produção" short={a.prod?.short} subject={a.prod?.subject} date={a.prod?.date} dirty={a.prod?.dirty} />
                <Ref label="Dev" short={a.dev?.short} subject={a.dev?.subject} date={a.dev?.date} dirty={a.dev?.dirty} />
                <Ref label="Gitea" short={a.gitea?.short} subject={a.branch} />
              </div>

              {a.pending.length > 0 && (
                <div className="mt-1 pt-2 border-t border-border/40">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                    Aguardando deploy
                  </span>
                  <ul className="mt-1.5 flex flex-col gap-1">
                    {a.pending.map(c => (
                      <li key={c.short} className="flex items-baseline gap-2 min-w-0">
                        <FiGitCommit className="w-3 h-3 text-muted-foreground shrink-0" />
                        <code className="text-[10px] font-mono text-muted-foreground shrink-0">{c.short}</code>
                        <span className="text-[11px] text-foreground truncate" title={c.subject}>{c.subject}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
